import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  Container, // Added Container for max-width control
  Pagination
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CreditCardItem from "../components/common/CreditCardItem"; // Make sure path is correct
import buxferService from "../services/buxfer.service";

// --- HELPERS ---
// Visual id generator replaced with a simple prefix + customer_code when available.
const generateVisualId = (customer) => {
  if (!customer) return "UNKNOWN";
  if (customer.customer_code) return `${customer.customer_code}`;
  return `${(customer.full_name || 'UNKNOWN').substring(0,6).toUpperCase()}`;
};

// Encode customer_code or fallback to the customer identifier string
const encodeCustomerCode = (code) => {
  try {
    return code ? encodeURIComponent(code) : '';
  } catch (e) {
    return code || '';
  }
};

export default function CreditCardCustomerPage() {
  const navigate = useNavigate();
  const [customerCards, setCustomerCards] = useState([]);
  
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All Status");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalCount, setTotalCount] = useState(0);
  const [paramsApplied, setParamsApplied] = useState(false); // Track if URL params have been applied
  
  // Hardcoded status list for dropdown
  const statusOptions = ["All Status", "Active", "Suspended", "Not Managed", "Canceling", "Low Credit", "Cleared", "Pending"];

    const handleViewDetails = (customer = {}) => {
      const code = customer.customer_code;
      if (!code) {
          console.warn("No customer_code found for customer", customer);
          return;
      }
      const path = `/single-customer/${encodeCustomerCode(code)}`;
      navigate(path);
    };

  // Parse URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchParam = params.get('search') || '';
    const statusParam = params.get('status') || 'All Status';
    const pageParam = parseInt(params.get('page') || '1', 10);

    setSearch(searchParam);
    setStatus(statusParam);
    setPage(pageParam - 1); // Convert 1-based to 0-based
    setParamsApplied(true);
  }, []);

  // Update URL when search, status, or page change
  useEffect(() => {
    if (!paramsApplied) return; // Don't update URL during initial mount
    
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    params.set('status', status);
    params.set('page', page + 1); // Convert 0-based to 1-based for URL
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, [search, status, page, paramsApplied]);

  useEffect(() => {
    const statusFilter = status === "All Status" ? null : status;

    // Use server-side pagination
    (async () => {
      try {
        const resp = await buxferService.getCreditCardCustomers(search, statusFilter, page + 1, rowsPerPage);
        let rows = [];
        let count = 0;
        if (resp && resp.success && resp.result) {
          rows = resp.result.rows || [];
          count = resp.result.count || 0;
        } else if (Array.isArray(resp)) {
          rows = resp;
          count = resp.length;
        }

        // Keep backend filtering responsibility; present rows as received
        setCustomerCards(rows);
        setTotalCount(count || rows.length);
      } catch (err) {
        console.error('Failed to fetch credit card customers', err);
        setCustomerCards([]);
        setTotalCount(0);
      }
    })();
  }, [search, status, page, rowsPerPage]);

  return (
    <Container maxWidth="xl" sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Credit Card Management
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        View credit card information, track payments, and monitor account details
      </Typography>
      

      {/* Search + Filter */}
      <Box display="flex" gap={2} mb={4}>
        <TextField
          size="small"
          placeholder="Search by customer name, login, or bank..."
          fullWidth
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(0); // Reset to first page when search changes
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: 'background.paper' }}
        />
        <TextField 
            select 
            size="small" 
            value={status} 
            onChange={e => {
              setStatus(e.target.value);
              setPage(0); // Reset to first page when status changes
            }} 
            sx={{ width: 200, bgcolor: 'background.paper' }}
        >
          {statusOptions.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Cards Grid */}
      {/* alignItems="stretch" ensures all cards in the same row have equal height */}
      <Grid container spacing={3} alignItems="stretch">
        {customerCards.map((group, index) => {
          const { customer, cards } = group;
          const totalBalance = cards.reduce((sum, c) => sum + Math.abs(c.balance || 0), 0);
          
          // Truncate bank list if too long
          let bankList = [...new Set(cards.map(c => c.bank))].join(", ");
          //if (bankList.length > 30) bankList = bankList.substring(0, 30) + "...";

          const visualId = generateVisualId(customer);

          return (
            // Responsive breakpoints: 1 col mobile, 2 cols tablet, 3 cols laptop, 3 cols wide screen
            <Grid item xs={12} sm={4} md={4} lg={4} key={index}>
              <CreditCardItem
                card={{
                  id: visualId,
                  name: customer.full_name || customer.cc_email,
                  bank: bankList,
                  cardType: `${cards.length} Accounts`, 
                  balance: totalBalance,
                  status: customer.status || "Active",
                  // Add dummy last payment data if your API doesn't provide it yet
                  lastPayment: customer.last_payment || null 
                }}
                onViewDetails={() => handleViewDetails(customer)}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Pagination Controls */}
      <Box display="flex" justifyContent="center" mt={4}>
        <Pagination
          count={Math.max(1, Math.ceil(totalCount / rowsPerPage))}
          page={page + 1}
          onChange={(e, val) => setPage(val - 1)}
          color="primary"
        />
      </Box>
    </Container>
  );
}